import type { ComponentProps } from 'react';
import { memo } from 'react';

import GamepadIcon from '@/assets/icons/gamepad.svg?react';
import SparkleFit from '@/assets/icons/sparkle-fit.svg?react';
import StarIcon from '@/assets/icons/star.svg?react';
import { Separator } from '@/shared/components/ui/separator';
import type { Rarity } from '@/shared/types/rarity';
import { cn } from '@/shared/utils/cn';
import { rarityFitLabels, rarityTextColors } from '@/shared/utils/rarity';

export interface ProfileOverviewCardCompatibilityDetailsProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  rarity: Rarity;
  percent: number;
  vibeMatch: number | undefined;
  playstyleMatch: number | undefined;
}

export const ProfileOverviewCardCompatibilityDetails = memo(
  function ProfileOverviewCardCompatibilityDetails({
    rarity,
    percent,
    vibeMatch,
    playstyleMatch,
    className,
    ...props
  }: ProfileOverviewCardCompatibilityDetailsProps) {
    const showSeparator =
      vibeMatch !== undefined && playstyleMatch !== undefined;

    return (
      <div
        className={cn(
          'bg-titan-900 flex flex-col gap-3 rounded-xl px-2 py-3',
          className,
        )}
        {...props}
      >
        {/* Fit row */}
        <p
          className={cn(
            'flex items-center justify-center gap-1.5 text-base leading-[1.4] font-black',
            rarityTextColors[rarity],
          )}
        >
          <SparkleFit className="size-4" />
          {rarityFitLabels[rarity]} {Math.round(percent * 100)}%
          <SparkleFit className="size-4" />
        </p>

        {/* Vibe & Playstyle row */}
        {(vibeMatch !== undefined || playstyleMatch !== undefined) && (
          <div className="flex h-6 items-center gap-2">
            {vibeMatch !== undefined && (
              <div className="flex flex-1 items-center justify-center gap-2">
                <StarIcon className="text-cold-300 size-5 shrink-0" />
                <span className="text-xs font-medium md:text-sm">
                  <span className="text-cold-300">
                    {Math.round(vibeMatch * 100)}%
                  </span>{' '}
                  <span className="text-cold-500 text-[10px] md:text-xs">
                    Vibe
                  </span>
                </span>
              </div>
            )}
            {showSeparator && (
              <Separator orientation="vertical" className="h-6" />
            )}
            {playstyleMatch !== undefined && (
              <div className="flex flex-1 items-center justify-center gap-2">
                <GamepadIcon className="text-cold-300 size-5 shrink-0" />
                <span className="text-xs font-medium md:text-sm">
                  <span className="text-cold-300">
                    {Math.round(playstyleMatch * 100)}%
                  </span>{' '}
                  <span className="text-cold-500 text-[10px] md:text-xs">
                    Playstyle
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);
